import {
	setSelectedLocation,
	updateLocationSelector,
} from "./location-state.js";

function safeJsonParse(value, fallback = null) {
	if (!value) {
		return fallback;
	}

	try {
		return JSON.parse(value);
	} catch (error) {
		console.error("Failed to parse JSON payload", error);
		return fallback;
	}
}

const moduleData = safeJsonParse(
	document.getElementById("wp-script-module-data-@single-location-page")
		?.textContent,
	{},
);

function loadGoogleMaps(apiKey) {
	if (!apiKey) {
		return Promise.reject(new Error("Missing Google Maps API key."));
	}

	if (window.google?.maps?.importLibrary) {
		return Promise.resolve(window.google.maps);
	}

	return new Promise((resolve, reject) => {
		const existingScript = document.querySelector(
			'script[data-location-map-loader="true"]',
		);

		if (existingScript) {
			existingScript.addEventListener("load", () => resolve(window.google.maps));
			existingScript.addEventListener("error", () =>
				reject(new Error("Google Maps failed to load.")),
			);
			return;
		}

		const callbackName = `teresasPizzaSingleLocationMapInit${Date.now()}`;
		window[callbackName] = () => {
			resolve(window.google.maps);
			delete window[callbackName];
		};

		const script = document.createElement("script");
		script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly&loading=async&callback=${callbackName}`;
		script.async = true;
		script.defer = true;
		script.dataset.locationMapLoader = "true";
		script.addEventListener("error", () => {
			reject(new Error("Google Maps failed to load."));
			delete window[callbackName];
		});
		document.head.appendChild(script);
	});
}

async function initializeMap() {
	const mapElement = document.getElementById("location-map");
	const apiKey = moduleData.frontend_google_maps_api_key;

	if (!mapElement || !apiKey) {
		return;
	}

	const lat = Number.parseFloat(mapElement.dataset.lat ?? "");
	const lng = Number.parseFloat(mapElement.dataset.lng ?? "");
	if (Number.isNaN(lat) || Number.isNaN(lng)) {
		return;
	}

	try {
		await loadGoogleMaps(apiKey);
		const { Map } = await google.maps.importLibrary("maps");
		const { AdvancedMarkerElement, PinElement } =
			await google.maps.importLibrary("marker");

		const primaryColor = getComputedStyle(
			document.documentElement,
		).getPropertyValue("--accent");
		const primaryColorLight = getComputedStyle(
			document.documentElement,
		).getPropertyValue("--accent-light");

		const map = new Map(mapElement, {
			center: { lat, lng },
			zoom: 14,
			mapId: "single-location-page-map",
			gestureHandling: "cooperative",
		});

		const pin = new PinElement({
			background: primaryColor.trim() || "#a51f15",
			borderColor: primaryColorLight.trim() || "#d87a66",
			glyphColor: "#fff",
		});

		new AdvancedMarkerElement({
			map,
			position: { lat, lng },
			title: mapElement.dataset.name ?? "",
			content: pin.element,
		});
	} catch (error) {
		console.error("Failed to initialize location map.", error);
	}
}

function initializeLocationSelection() {
	const makeMyLocationButton = document.getElementById(
		"make-my-location-button",
	);
	const location = moduleData.location;

	if (!makeMyLocationButton || !location?.id) {
		return;
	}

	makeMyLocationButton.addEventListener("click", () => {
		setSelectedLocation(location);
		updateLocationSelector();
		window.location.reload();
	});
}

// The page itself is cached (nginx/CDN) so its server-rendered "Open now/
// Closed" badge can be stale. Refresh it from an always-fresh endpoint after
// load instead of forcing the whole page to skip caching. A cache-busting
// query param guarantees a fresh request even if the cache layer ignores the
// endpoint's own no-store header (see LocationStatusApiController).
async function refreshLocationStatus() {
	const container = document.getElementById("location-status-row");
	const locationId = moduleData.location?.id;

	if (!container || !locationId) {
		return;
	}

	let status;
	try {
		const response = await fetch(
			`/wp-json/teresas-pizza/v1/location-status?location_id=${locationId}&_=${Date.now()}`,
			{ cache: "no-store" },
		);
		if (!response.ok) {
			return;
		}
		status = await response.json();
	} catch (error) {
		console.error("Failed to refresh location status.", error);
		return;
	}

	if (!status?.state || status.state === "unknown") {
		return;
	}

	container.replaceChildren();

	const statusText = document.createElement("span");
	statusText.className = `open-status-text ${status.state === "open" ? "is-open" : "is-closed"}`;
	statusText.textContent = status.label;
	container.appendChild(statusText);

	if (status.detail) {
		const hoursText = document.createElement("span");
		hoursText.className = "hours-text";
		hoursText.textContent = status.detail;
		container.appendChild(hoursText);
	}
}

document.addEventListener("DOMContentLoaded", () => {
	initializeLocationSelection();
	initializeMap();
	refreshLocationStatus();
});
